import { lazy, Suspense } from 'react';
import type { ReactNode } from 'react';
import { createBrowserRouter } from 'react-router-dom';
import { RequireSession } from '~/features/account/RequireSession';
import { SignInPage } from '~/features/account/SignInPage';
import { ApplicationDetailPage } from '~/features/applications/ApplicationDetailPage';
import { BrowsePage } from '~/features/marketplace/BrowsePage';
import { HomePage } from '~/features/marketplace/HomePage';
import { NotFoundPage } from '~/shared/pages/NotFoundPage';
import { AppShell } from '~/shared/ui/AppShell';
import { SkeletonGrid, Spinner } from '~/shared/ui';

/*
 * Everything a visitor needs on a first visit is in the main bundle; everything behind a sign-in is
 * split out. A developer console and an install wizard are large, and a visitor browsing the
 * catalog should not download either.
 */
const InstallWizardPage = lazy(async () => ({
  default: (await import('~/features/installation/InstallWizardPage')).InstallWizardPage,
}));
const InstallationsPage = lazy(async () => ({
  default: (await import('~/features/installation/InstallationsPage')).InstallationsPage,
}));
const InstallationDetailPage = lazy(async () => ({
  default: (await import('~/features/installation/InstallationDetailPage')).InstallationDetailPage,
}));
const DeveloperConsolePage = lazy(async () => ({
  default: (await import('~/features/developer/DeveloperConsolePage')).DeveloperConsolePage,
}));
const DeveloperApplicationPage = lazy(async () => ({
  default: (await import('~/features/developer/DeveloperApplicationPage')).DeveloperApplicationPage,
}));
const NewApplicationPage = lazy(async () => ({
  default: (await import('~/features/developer/NewApplicationPage')).NewApplicationPage,
}));
const RegisterDeveloperPage = lazy(async () => ({
  default: (await import('~/features/developer/RegisterDeveloperPage')).RegisterDeveloperPage,
}));
const PublishGuidePage = lazy(async () => ({
  default: (await import('~/features/developer/PublishGuidePage')).PublishGuidePage,
}));
const CryptoPaymentsInfoPage = lazy(async () => ({
  default: (await import('~/features/marketplace/CryptoPaymentsInfoPage')).CryptoPaymentsInfoPage,
}));
const CryptoCheckoutPage = lazy(async () => ({
  default: (await import('~/features/marketplace/CryptoCheckoutPage')).CryptoCheckoutPage,
}));
const AccountPage = lazy(async () => ({
  default: (await import('~/features/account/AccountPage')).AccountPage,
}));
const DeveloperProfilePage = lazy(async () => ({
  default: (await import('~/features/applications/DeveloperProfilePage')).DeveloperProfilePage,
}));

function Deferred({ children, grid = false }: { children: ReactNode; grid?: boolean }): JSX.Element {
  return (
    <Suspense
      fallback={
        grid ? (
          <SkeletonGrid />
        ) : (
          <div className="grid place-items-center py-24 text-slate-500">
            <Spinner className="h-6 w-6" />
          </div>
        )
      }
    >
      {children}
    </Suspense>
  );
}

export const router = createBrowserRouter([
  {
    path: '/',
    element: <AppShell />,
    children: [
      { path: 'signin', element: <SignInPage /> },
      { index: true, element: <HomePage /> },
      { path: 'applications', element: <BrowsePage /> },
      { path: 'applications/:slug', element: <ApplicationDetailPage /> },
      {
        path: 'payments',
        element: (
          <Deferred>
            <CryptoPaymentsInfoPage />
          </Deferred>
        ),
      },
      {
        path: 'applications/:slug/pay',
        element: (
          <RequireSession>
            <Deferred>
              <CryptoCheckoutPage />
            </Deferred>
          </RequireSession>
        ),
      },
      {
        path: 'publish',
        element: (
          <Deferred>
            <PublishGuidePage />
          </Deferred>
        ),
      },
      {
        path: 'developers/:slug',
        element: (
          <Deferred grid>
            <DeveloperProfilePage />
          </Deferred>
        ),
      },

      {
        path: 'applications/:slug/install',
        element: (
          <RequireSession>
            <Deferred>
              <InstallWizardPage />
            </Deferred>
          </RequireSession>
        ),
      },
      {
        path: 'installations',
        element: (
          <RequireSession>
            <Deferred>
              <InstallationsPage />
            </Deferred>
          </RequireSession>
        ),
      },
      {
        path: 'installations/:installationId',
        element: (
          <RequireSession>
            <Deferred>
              <InstallationDetailPage />
            </Deferred>
          </RequireSession>
        ),
      },
      {
        path: 'account',
        element: (
          <RequireSession>
            <Deferred>
              <AccountPage />
            </Deferred>
          </RequireSession>
        ),
      },

      {
        path: 'developer',
        element: (
          <RequireSession>
            <Deferred>
              <DeveloperConsolePage />
            </Deferred>
          </RequireSession>
        ),
      },
      {
        path: 'developer/register',
        element: (
          <RequireSession>
            <Deferred>
              <RegisterDeveloperPage />
            </Deferred>
          </RequireSession>
        ),
      },
      {
        path: 'developer/applications/new',
        element: (
          <RequireSession>
            <Deferred>
              <NewApplicationPage />
            </Deferred>
          </RequireSession>
        ),
      },
      {
        path: 'developer/applications/:applicationId',
        element: (
          <RequireSession>
            <Deferred>
              <DeveloperApplicationPage />
            </Deferred>
          </RequireSession>
        ),
      },

      { path: '*', element: <NotFoundPage /> },
    ],
  },
]);
